/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { t } from '@sportninja/common/i18n'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import Text from 'src/components/ts-components/Text'
import Button from 'src/components/ts-components/Button'
import { TextInput } from 'src/components/ts-components/TextInput'
import { DateOfBirthInput } from 'src/components/ts-components/DateOfBirthInput'
import { Checkbox } from 'antd'
import ModernPhoneInput from 'src/components/ts-components/ModernPhoneInput'

/**
 * Props for the ParentStep component
 * @typedef {Object} ParentStepProps
 * @property {Object} data - The invitation data
 * @property {Function} onNext - Function to call when proceeding to next step
 * @property {Array<string>} fieldErrors - Array of field names with errors
 * @property {Object} customError - Custom error messages by field name
 * @property {string} userName - Parent's first name
 * @property {string} userLastName - Parent's last name
 * @property {string} userDOB - Parent's date of birth
 * @property {string} userEmail - Parent's email
 * @property {string} userPhone - Parent's phone number
 * @property {string} password - Parent's password
 * @property {string} confirmPassword - Parent's password confirmation
 * @property {boolean} acceptTerms - Whether parent has accepted terms
 * @property {Function} setUserName - Function to set parent's first name
 * @property {Function} setUserLastName - Function to set parent's last name
 * @property {Function} setUserDOB - Function to set parent's date of birth
 * @property {Function} setUserEmail - Function to set parent's email
 * @property {Function} setUserPhone - Function to set parent's phone number
 * @property {Function} setPassword - Function to set parent's password
 * @property {Function} setConfirmPassword - Function to set parent's password confirmation
 * @property {Function} setAcceptTerms - Function to set whether parent has accepted terms
 * @property {Function} onBack - Function to go back to previous step
 * @property {Array<number>} stepState - Array tracking the step history
 */
type ParentStepProps = {
  data: any
  onNext: () => void
  fieldErrors?: Record<string, string>
  customError?: Record<string, string>
  userName: string
  userLastName: string
  userDOB: string
  userEmail: string
  userPhone: string
  password: string
  confirmPassword: string
  acceptTerms: boolean
  setUserName: (value: string) => void
  setUserLastName: (value: string) => void
  setUserDOB: (value: string) => void
  setUserEmail: (value: string) => void
  setUserPhone: (value: string) => void
  setPassword: (value: string) => void
  setConfirmPassword: (value: string) => void
  setAcceptTerms: (value: boolean) => void
  onBack: () => void
  stepState: number[]
}

/**
 * ParentStep component for the underage invitation flow
 * Allows parent to create their profile
 *
 * @param {ParentStepProps} props - Component props
 * @returns {JSX.Element} The rendered component
 */
const ParentStep = ({
  onNext,
  fieldErrors = {},
  customError = {},
  userName,
  userLastName,
  userDOB,
  userPhone,
  password,
  confirmPassword,
  acceptTerms,
  setUserName,
  setUserLastName,
  setUserDOB,
  setUserPhone,
  setPassword,
  setConfirmPassword,
  setAcceptTerms,
  onBack,
  stepState,
}: ParentStepProps) => {
  /*
   * The shared picker reports E.164. The signup request has always stored the
   * same shape the old field wrote: a leading "+" and digits only, or empty
   * when the field is cleared.
   */
  const handlePhoneChange = (value: string) => {
    const digits = value.replace(/\D/g, '')
    setUserPhone(digits ? `+${digits}` : '')
  }

  return (
    <div css={containerStyle}>
      <Text
        variant='heading3'
        weight='semiBold'
        color={colors.NEUTRAL_0}
        style={'text-align: center;'}
      >
        {capitalizeWords(`Welcome To ${t('common:appName', {})}`)}
      </Text>
      <Text
        variant='paragraphMedium'
        weight='regular'
        color={colors.NEUTRAL_50}
        style={'text-align: center;'}
      >
        {"Ok, now let's create your user profile:"}
      </Text>

      <form css={formStyle}>
        <div css={rowStyle}>
          <div css={halfColumnStyle}>
            <TextInput
              name='name_first'
              customError={customError}
              fieldErrors={fieldErrors}
              label={capitalizeWords('Your First Name')}
              setValue={(value) => setUserName(value)}
              value={userName}
              placeholder={capitalizeWords('Your First Name')}
              autoComplete='given-name'
              required
            />
          </div>
          <div css={halfColumnStyle}>
            <TextInput
              name='name_last'
              customError={customError}
              fieldErrors={fieldErrors}
              label={capitalizeWords('Your Last Name')}
              setValue={(value) => setUserLastName(value)}
              value={userLastName}
              placeholder={capitalizeWords('Your Last Name')}
              autoComplete='family-name'
              required
            />
          </div>
        </div>

        <div css={rowStyle}>
          <div css={halfColumnStyle}>
            <DateOfBirthInput
              name='birth_date'
              value={userDOB}
              setValue={(value) => setUserDOB(value)}
              label={capitalizeWords('Your Birth Date')}
              fieldErrors={fieldErrors}
              customError={customError}
            />
          </div>
          <div css={halfColumnStyle}>
            <ModernPhoneInput
              label={capitalizeWords('Phone Number')}
              value={userPhone}
              onChange={handlePhoneChange}
              forcedDefaultCountry='CA'
              error={customError?.phone}
            />
          </div>
        </div>

        <div css={rowStyle}>
          <div css={halfColumnStyle}>
            <TextInput
              name='password'
              label={capitalizeWords('Password')}
              value={password}
              setValue={(value) => setPassword(value)}
              fieldErrors={fieldErrors}
              customError={customError}
              type='password'
              placeholder={capitalizeWords('Password')}
              autoComplete='new-password'
              required
            />
          </div>
          <div css={halfColumnStyle}>
            <TextInput
              name='confirm_password'
              label={capitalizeWords('Confirm Password')}
              value={confirmPassword}
              setValue={(value) => setConfirmPassword(value)}
              fieldErrors={fieldErrors}
              customError={customError}
              type='password'
              placeholder={capitalizeWords('Confirm Password')}
              autoComplete='new-password'
              required
            />
          </div>
        </div>

        <div css={termsRowStyle}>
          <Checkbox
            checked={acceptTerms}
            onChange={(e) => setAcceptTerms(e.target.checked)}
          />
          <Text variant='paragraphSmall' weight='regular' color={colors.WHITE}>
            I agree to the{' '}
            <a href='/docs/eula.pdf' target='_blank' css={termsLinkStyle}>
              Terms &amp; Conditions
            </a>{' '}
            and the{' '}
            <a href='/docs/privacy.pdf' target='_blank' css={termsLinkStyle}>
              Privacy Policy
            </a>
            .
          </Text>
        </div>
      </form>

      <div css={footerStyle}>
        {stepState.length > 0 && (
          <div css={backColumnStyle}>
            <Button text='Back' variant='secondary' onClick={onBack} />
          </div>
        )}
        <div css={nextColumnStyle}>
          <Button
            variant='primary'
            text='Create Your User'
            onClick={onNext}
            disabled={!acceptTerms}
          />
        </div>
      </div>
    </div>
  )
}

export default ParentStep

// ---- styled ----
/*
 * The flow's shell owns this step's page padding, centering and width cap, so
 * the container only stacks its own children. Rows are spaced with a gap
 * rather than per-row margins, so the conditionally rendered Back button
 * closes its own space.
 */
const containerStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`

const formStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`

const rowStyle = css`
  display: flex;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`

// min-width: 0 lets the flex children actually shrink — without it their
// intrinsic input width keeps the row from narrowing and forces overflow.
const halfColumnStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 50%;
  min-width: 0;
  @media (max-width: 768px) {
    width: 100%;
  }
`

const termsRowStyle = css`
  display: flex;
  gap: 8px;
  align-items: center;
`

const termsLinkStyle = css`
  cursor: pointer;
  color: ${colors.NEUTRAL_0};
  text-decoration: underline;
  text-underline-offset: 2px;
`

const footerStyle = css`
  display: flex;
  gap: 8px;
  width: 100%;
  @media (max-width: 768px) {
    flex-direction: column-reverse;
  }
`

const backColumnStyle = css`
  display: flex;
  flex: 1;
  min-width: 0;
`

const nextColumnStyle = css`
  display: flex;
  flex: 1;
`
